import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  createTask,
  getTaskDetail,
  fetchTaskProjectOptionsPage,
  listTaskUserOptions,
  updateTask,
  validateTaskCompletion,
} from '../api/taskApi'
import { getProjectDetail } from '../../project/api/projectApi'
import { getTaskMasterDropdown } from '../../../masters/task-master/api/taskMasterApi'
import { appToast } from '../../../../components/AppToast'
import { deriveTaskStatus } from '../../common/pmStatusUtils'

const MIN_DATE = '1900-01-01'
const MAX_DATE = '9999-12-31'

const PRIORITIES = ['HIGH', 'MEDIUM', 'LOW']
const STATUSES = ['DRAFT', 'IN_PROGRESS', 'COMPLETED', 'CLOSED']

function toDateInput(value) {
  if (!value) return ''
  const raw = String(value)
  const datePart = raw.match(/^(\d{4}-\d{2}-\d{2})/)
  if (datePart) return datePart[1]
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  const y = parsed.getFullYear()
  const m = String(parsed.getMonth() + 1).padStart(2, '0')
  const d = String(parsed.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function toDateTimeInput(value) {
  if (!value) return undefined
  return `${value}T00:00:00`
}

function normalizeText(value) {
  const text = String(value || '').trim()
  return text.length ? text : undefined
}

function normalizeIdList(value) {
  if (!Array.isArray(value)) return []
  return Array.from(
    new Set(
      value
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item) && item > 0),
    ),
  )
}

function parseTextList(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

function clampMinutes(value) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric < 0) return 0
  return Math.min(59, Math.floor(numeric))
}

function normalizeHourMinuteInput(value) {
  const text = String(value ?? '').replace(/[^\d]/g, '')
  return text.length ? text : '0'
}

function splitDecimalHours(value) {
  const numeric = Number(value || 0)
  if (!Number.isFinite(numeric) || numeric <= 0) return { hours: '0', minutes: '0' }
  const roundedMinutes = Math.round(numeric * 60)
  const normalizedHours = Math.floor(roundedMinutes / 60)
  const normalizedMinutes = roundedMinutes % 60
  return { hours: String(normalizedHours), minutes: String(normalizedMinutes) }
}

function combineHoursAndMinutes(hoursValue, minutesValue) {
  const hours = Math.max(0, Math.floor(Number(hoursValue || 0) || 0))
  const minutes = clampMinutes(minutesValue)
  const total = hours + minutes / 60
  return Number(total.toFixed(2))
}

function nextPriority(current, direction) {
  const currentIndex = PRIORITIES.findIndex((item) => item === current)
  if (currentIndex === -1) return current
  const nextIndex = Math.min(PRIORITIES.length - 1, Math.max(0, currentIndex + direction))
  return PRIORITIES[nextIndex]
}

function normalizeResponsibleDependencies(value) {
  if (!Array.isArray(value)) return []
  const seen = new Set()
  const rows = []
  value.forEach((item, index) => {
    if (!item || typeof item !== 'object') return
    const userId = Number(item.user_id)
    if (!Number.isFinite(userId) || userId <= 0 || seen.has(userId)) return
    seen.add(userId)
    rows.push({
      user_id: userId,
      user_name: String(item.user_name || '').trim() || 'User',
      assignment_note: String(item.assignment_note || ''),
      is_completed: Number(item.is_completed) ? 1 : 0,
      priority_order: Number(item.priority_order) > 0 ? Number(item.priority_order) : index + 1,
    })
  })
  rows.sort((a, b) => Number(a.priority_order || 0) - Number(b.priority_order || 0))
  return rows.map((row, index) => ({ ...row, priority_order: index + 1 }))
}

function deriveStatus(startDate, isCompleted, currentStatus) {
  return deriveTaskStatus(startDate, isCompleted, currentStatus)
}

export function useTaskEntryScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const editingTaskId = searchParams.get('id')
  const returnUrl = location.state?.returnTo || '/nerve/task/list'
  const preselectedProjectId = searchParams.get('projectId')

  const savingRef = useRef(false)

  const [form, setForm] = useState({
    task_name: '',
    task_description: '',
    project_id: editingTaskId ? '' : String(preselectedProjectId || ''),
    priority: 'HIGH',
    status: 'DRAFT',
    planned_start: '',
    planned_end: '',
    assigned_to_csv: [],
    dependency_task_ids: [],
    responsible_dependencies: [],
    effort_estimate: '1',   // synced decimal hours for backend payloads
    effort_hours: '1',      // default 4h for new tasks
    effort_minutes: '0',
    is_completed: 0,
    completion_note: '',
    attachmentsText: '',
    tagsText: '',
    task_master_id: '',
    create_template_jobs: true,
  })

  const [complexityPoints, setComplexityPoints] = useState(5)
  const [initialProjectName, setInitialProjectName] = useState('')
  const [userOptions, setUserOptions] = useState([])
  const [taskOptions, setTaskOptions] = useState([])
  const [taskMasterOptions, setTaskMasterOptions] = useState([])
  const [optionError, setOptionError] = useState('')
  const [isLoading, setIsLoading] = useState(Boolean(editingTaskId))
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const fetchProjectOptions = useCallback((search, page, pageSize) => {
    return fetchTaskProjectOptionsPage(search, page, pageSize)
  }, [])

  useEffect(() => {
    let active = true

    async function loadStaticOptions() {
      try {
        const taskMasters = await getTaskMasterDropdown()
        if (!active) return

        setTaskMasterOptions(Array.isArray(taskMasters) ? taskMasters : [])
        setUserOptions([])
        setTaskOptions([])
        setOptionError('')
      } catch {
        if (!active) return
        setTaskMasterOptions([])
        setUserOptions([])
        setTaskOptions([])
      }
    }

    async function loadTaskIfEditing() {
      if (!editingTaskId) return
      const detail = await getTaskDetail(editingTaskId)
      if (!active) return

      setForm({
        task_name: detail?.task_name || '',
        task_description: detail?.task_description || '',
        project_id: detail?.project_id ? String(detail.project_id) : '',
        priority: String(detail?.priority || 'MEDIUM').toUpperCase(),
        status: detail?.status || 'DRAFT',
        planned_start: toDateInput(detail?.planned_start),
        planned_end: toDateInput(detail?.planned_end),
        assigned_to_csv: normalizeIdList(detail?.assigned_to_user_ids).slice(0, 1),
        dependency_task_ids: normalizeIdList(detail?.dependency_task_ids),
        responsible_dependencies: normalizeResponsibleDependencies(
          Array.isArray(detail?.dependency_users) && detail.dependency_users.length
            ? detail.dependency_users
            : normalizeIdList(detail?.dependency_user_ids).map((user_id, index) => ({
              user_id,
              priority_order: index + 1,
              assignment_note: '',
              is_completed: 0,
            })),
        ),
        // Load effort_estimate directly from the API value
        effort_estimate: detail?.effort_estimate != null ? String(detail.effort_estimate) : '4',
        ...(() => {
          const splitEffort = splitDecimalHours(detail?.effort_estimate)
          return {
            effort_hours: splitEffort.hours,
            effort_minutes: splitEffort.minutes,
          }
        })(),
        is_completed: Number(detail?.is_completed || 0) ? 1 : 0,
        completion_note: detail?.completion_note || '',
        attachmentsText: Array.isArray(detail?.attachments) ? detail.attachments.join(', ') : '',
        tagsText: Array.isArray(detail?.tags) ? detail.tags.join(', ') : '',
        task_master_id: detail?.task_master_id ? String(detail.task_master_id) : '',
      })

      const selectedUserIds = normalizeIdList([
        ...(Array.isArray(detail?.assigned_to_user_ids) ? detail.assigned_to_user_ids : []),
        ...(Array.isArray(detail?.dependency_users)
          ? detail.dependency_users.map((row) => Number(row?.user_id)).filter((value) => Number.isFinite(value) && value > 0)
          : []),
        ...(Array.isArray(detail?.dependency_user_ids) ? detail.dependency_user_ids : []),
      ])
      if (selectedUserIds.length) {
        try {
          const selectedUsers = await listTaskUserOptions({
            page: 1,
            page_size: 200,
            user_ids: selectedUserIds.join(','),
            project_id: Number(detail?.project_id || 0) || undefined,
          })
          if (!active) return
          if (Array.isArray(selectedUsers) && selectedUsers.length) {
            setUserOptions((prev) => {
              const merged = [...(Array.isArray(prev) ? prev : []), ...selectedUsers]
              const map = new Map()
              merged.forEach((user) => {
                if (!user?.id) return
                map.set(String(user.id), user)
              })
              return Array.from(map.values())
            })
          }
        } catch {
          // Ignore hydration failures; fallback labels are handled in UI.
        }
      }
    }

    async function bootstrap() {
      setIsLoading(true)
      setError('')
      try {
        await Promise.all([loadStaticOptions(), loadTaskIfEditing()])
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load task configuration.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    bootstrap()
    return () => {
      active = false
    }
  }, [editingTaskId])

  // Preselect project ID if passed in query parameters on creation
  useEffect(() => {
    if (!editingTaskId && preselectedProjectId) {
      setForm((prev) => ({ ...prev, project_id: String(preselectedProjectId) }))
    }
  }, [editingTaskId, preselectedProjectId])

  useEffect(() => {
    let active = true
    async function loadProjectData() {
      const projectId = Number(form.project_id || 0)
      if (projectId <= 0) {
        setUserOptions([])
        setInitialProjectName('')
        return
      }
      try {
        const [users, projectDetail] = await Promise.all([
          listTaskUserOptions({
            page: 1,
            page_size: 200,
            project_id: projectId,
          }),
          getProjectDetail(projectId),
        ])
        if (!active) return
        setUserOptions(Array.isArray(users) ? users : [])
        if (projectDetail) {
          setInitialProjectName(projectDetail.name || '')
        }
      } catch {
        if (!active) return
        setUserOptions([])
      }
    }
    loadProjectData()
    return () => {
      active = false
    }
  }, [form.project_id])

  const completionPercent = useMemo(() => {
    const tracked = [
      form.task_name,
      form.project_id,
      form.priority,
      form.status,
      form.planned_start,
      form.assigned_to_csv.length ? 'assigned' : '',
      form.task_description,
    ]
    const filled = tracked.filter((value) => String(value || '').trim().length > 0).length
    return Math.round((filled / tracked.length) * 100)
  }, [form])

  const estimatedEffort = useMemo(() => {
    const base = combineHoursAndMinutes(form.effort_hours, form.effort_minutes)
    if (base > 0) return Number(base.toFixed(2))
    // Keep the legacy decimal field as a fallback for older edits.
    const direct = parseFloat(form.effort_estimate)
    if (Number.isFinite(direct) && direct > 0) return Number(direct.toFixed(2))
    return 4  // absolute fallback (4h default)
  }, [form.effort_estimate, form.effort_hours, form.effort_minutes])

  const riskLevel = useMemo(() => {
    const priority = String(form.priority || 'MEDIUM').toUpperCase()
    if (priority === 'HIGH' || complexityPoints >= 7) return 'High'
    if (priority === 'LOW' && complexityPoints <= 3) return 'Low'
    return 'Moderate'
  }, [complexityPoints, form.priority])

  function patchForm(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function setPriority(value) {
    if (!PRIORITIES.includes(value)) return
    patchForm('priority', value)
  }

  async function toggleCompleted() {
    const currentCompleted = form.is_completed
    const nextCompleted = currentCompleted ? 0 : 1

    // Block on new-task creation: no jobs or timesheets can exist yet
    if (nextCompleted === 1 && !editingTaskId) {
      appToast.error(
        'A new task cannot be marked as completed on creation. Create it first, then complete all jobs and have at least one timesheet approved.',
      )
      return
    }

    // Only validate when toggling TO completed on an existing (edit mode) task
    if (nextCompleted === 1 && editingTaskId) {
      try {
        const validation = await validateTaskCompletion(editingTaskId)
        if (!validation.valid) {
          const errors = Array.isArray(validation.errors) ? validation.errors : []
          errors.forEach((msg) => appToast.error(msg))
          if (!errors.length) {
            appToast.error('Task cannot be marked as completed. Please check all requirements.')
          }
          return // block the toggle
        }
      } catch {
        // Non-blocking: if validation call fails, allow the toggle (server will validate on save)
      }
    }

    setForm((prev) => {
      const nextStatus = deriveStatus(prev.planned_start, nextCompleted)
      return {
        ...prev,
        is_completed: nextCompleted,
        status: nextStatus,
      }
    })
  }

  useEffect(() => {
    if (editingTaskId) return
    if (Number(form.is_completed) === 1) {
      setForm((prev) => (prev.status === 'COMPLETED' ? prev : { ...prev, status: 'COMPLETED' }))
    }
  }, [form.is_completed, editingTaskId])

  function addAssignee(userId) {
    const normalized = Number(userId)
    if (!normalized) return
    setForm((prev) => ({
      ...prev,
      assigned_to_csv: [normalized],
      is_pool: false,
    }))
  }

  function assignToPool() {
    setForm((prev) => ({
      ...prev,
      assigned_to_csv: [],
      is_pool: true,
    }))
  }

  function removeAssignee(userId) {
    const normalized = Number(userId)
    setForm((prev) => ({
      ...prev,
      assigned_to_csv: prev.assigned_to_csv.filter((value) => Number(value) !== normalized),
      is_pool: true,
    }))
  }

  function addDependencyTask(taskId) {
    const normalized = Number(taskId)
    if (!normalized) return
    if (editingTaskId && Number(editingTaskId) === normalized) return
    setForm((prev) => ({
      ...prev,
      dependency_task_ids: normalizeIdList([...prev.dependency_task_ids, normalized]),
    }))
  }

  function removeDependencyTask(taskId) {
    const normalized = Number(taskId)
    setForm((prev) => ({
      ...prev,
      dependency_task_ids: prev.dependency_task_ids.filter((value) => Number(value) !== normalized),
    }))
  }

  function addResponsibleUser(userId) {
    const normalized = Number(userId)
    if (!normalized) return
    setForm((prev) => ({
      ...prev,
      responsible_dependencies: normalizeResponsibleDependencies([
        ...prev.responsible_dependencies,
        {
          user_id: normalized,
          priority_order: prev.responsible_dependencies.length + 1,
          assignment_note: '',
          is_completed: 0,
        },
      ]),
    }))
  }

  function removeResponsibleUser(userId) {
    const normalized = Number(userId)
    setForm((prev) => ({
      ...prev,
      responsible_dependencies: normalizeResponsibleDependencies(
        prev.responsible_dependencies.filter((row) => Number(row.user_id) !== normalized),
      ),
    }))
  }

  function updateResponsibleNote(userId, note) {
    const normalized = Number(userId)
    setForm((prev) => ({
      ...prev,
      responsible_dependencies: normalizeResponsibleDependencies(
        prev.responsible_dependencies.map((row) =>
          Number(row.user_id) === normalized
            ? {
              ...row,
              assignment_note: String(note || ''),
            }
            : row,
        ),
      ),
    }))
  }

  function toggleResponsibleCompleted(userId) {
    const normalized = Number(userId)
    setForm((prev) => ({
      ...prev,
      responsible_dependencies: normalizeResponsibleDependencies(
        prev.responsible_dependencies.map((row) =>
          Number(row.user_id) === normalized
            ? {
              ...row,
              is_completed: Number(row.is_completed) ? 0 : 1,
            }
            : row,
        ),
      ),
    }))
  }

  function syncResponsibleUsers(nextUserIds, userNameById = {}) {
    const normalizedIds = normalizeIdList(nextUserIds)
    setForm((prev) => {
      const existingById = new Map(
        normalizeResponsibleDependencies(prev.responsible_dependencies).map((row) => [Number(row.user_id), row]),
      )
      const nextRows = normalizedIds.map((userId, index) => {
        const existing = existingById.get(userId)
        return {
          user_id: userId,
          user_name: String(existing?.user_name || userNameById?.[String(userId)] || 'User').trim() || 'User',
          assignment_note: String(existing?.assignment_note || ''),
          is_completed: Number(existing?.is_completed) ? 1 : 0,
          priority_order: index + 1,
        }
      })
      return {
        ...prev,
        responsible_dependencies: normalizeResponsibleDependencies(nextRows),
      }
    })
  }

  function buildPayload() {
    const plannedStart = toDateTimeInput(form.planned_start)
    const plannedEnd = toDateTimeInput(form.planned_end)
    const effortEstimate = combineHoursAndMinutes(form.effort_hours, form.effort_minutes)
    const dependencyUsers = normalizeResponsibleDependencies(form.responsible_dependencies).map((row, index) => ({
      user_id: Number(row.user_id),
      priority_order: index + 1,
      assignment_note: String(row.assignment_note || '').trim(),
      is_completed: Number(row.is_completed) ? 1 : 0,
    }))
    const dependencyUserIds = dependencyUsers.map((row) => row.user_id)
    const canSendDependencyUsers =
      dependencyUsers.length === 0 || dependencyUsers.every((row) => String(row.assignment_note || '').trim().length > 0)

    const payload = {
      project_id: Number(form.project_id),
      task_name: normalizeText(form.task_name),
      task_description: normalizeText(form.task_description),
      assigned_to_csv: form.assigned_to_csv.length > 0 ? normalizeIdList(form.assigned_to_csv).slice(0, 1) : null,
      priority: String(form.priority || 'MEDIUM').toUpperCase(),
      planned_start: plannedStart,
      planned_end: plannedEnd,
      effort_estimate: effortEstimate,
      dependency_task_ids: normalizeIdList(form.dependency_task_ids),
      dependency_user_ids: dependencyUserIds,
      is_completed: Number(form.is_completed) ? 1 : 0,
      completion_note: normalizeText(form.completion_note),
      attachments: parseTextList(form.attachmentsText),
      tags: parseTextList(form.tagsText),
      task_master_id: form.task_master_id ? Number(form.task_master_id) : null,
      create_template_jobs: Boolean(form.task_master_id && form.create_template_jobs && !editingTaskId),
    }
    if (canSendDependencyUsers) {
      payload.dependency_users = dependencyUsers
    }
    return payload
  }

  async function handleSave(redirectToAllocate = false) {
    if (savingRef.current) return
    savingRef.current = true
    setError('')
    setSuccess('')

    if (!form.project_id) {
      setError('Project is required.')
      savingRef.current = false
      return
    }
    if (!String(form.task_name || '').trim()) {
      setError('Task name is required.')
      savingRef.current = false
      return
    }
    if (!form.planned_start) {
      setError('Start date is required.')
      savingRef.current = false
      return
    }
    if (!form.planned_end) {
      setError('End date is required.')
      savingRef.current = false
      return
    }
    if (!form.assigned_to_csv.length && !form.is_pool) {
      setError('Internal assignee is required (or select Unassigned / Task Pool).')
      savingRef.current = false
      return
    }
    // Validate effort estimate — must be a positive duration
    const effortVal = combineHoursAndMinutes(form.effort_hours, form.effort_minutes)
    if (!Number.isFinite(effortVal) || effortVal <= 0) {
      setError('Estimated effort is required and must be greater than 0.')
      savingRef.current = false
      return
    }
    if (['COMPLETED', 'CLOSED'].includes(String(form.status || '').toUpperCase()) && !String(form.completion_note || '').trim()) {
      setError('Completion note is required for COMPLETED/CLOSED status.')
      savingRef.current = false
      return
    }

    setIsSaving(true)
    try {
      const payload = buildPayload()
      if (editingTaskId) {
        await updateTask(editingTaskId, payload)
        setSuccess('Task updated successfully.')
      } else {
        await createTask(payload)
        setSuccess('Task created successfully.')
      }
      window.setTimeout(() => {
        savingRef.current = false
        setIsSaving(false)
        if (redirectToAllocate === true) {
          navigate('/allocation?mode=estimated')
        } else {
          navigate(returnUrl)
        }
      }, 250)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to save task configuration.'))
      savingRef.current = false
      setIsSaving(false)
    }
  }

  function handleSaveAndAllocate() {
    handleSave(true)
  }

  function handleDiscard() {
    navigate(returnUrl)
  }

  function handleTaskMasterChange(value) {
    const selectedId = Number(value)
    patchForm('task_master_id', value ? String(selectedId) : '')
    if (!value) {
      patchForm('create_template_jobs', false)
      return
    }
    patchForm('create_template_jobs', true)

    const selectedTemplate = taskMasterOptions.find((tm) => Number(tm.id) === selectedId)
    if (selectedTemplate) {
      if (selectedTemplate.default_priority) {
        patchForm('priority', String(selectedTemplate.default_priority).toUpperCase())
      }
      if (selectedTemplate.default_estimated_hours) {
        // Update both effort_estimate (direct input) and the legacy split fields
        patchForm('effort_estimate', String(selectedTemplate.default_estimated_hours))
        const splitEffort = splitDecimalHours(selectedTemplate.default_estimated_hours)
        patchForm('effort_hours', splitEffort.hours)
        patchForm('effort_minutes', splitEffort.minutes)
      }

      // Auto-fill Start Date to today if not already set
      setForm((prev) => {
        const today = new Date()
        const yyyy = today.getFullYear()
        const mm = String(today.getMonth() + 1).padStart(2, '0')
        const dd = String(today.getDate()).padStart(2, '0')
        const todayStr = `${yyyy}-${mm}-${dd}`

        const startDate = prev.planned_start || todayStr

        // Compute SLA end date: ceil(sla_hours / 24) calendar days, minimum 1
        let endDate = prev.planned_end
        const slaHours = Number(selectedTemplate.sla_hours || 0)
        if (slaHours > 0) {
          const slaDays = Math.max(1, Math.ceil(slaHours / 24))
          const start = new Date(startDate)
          start.setDate(start.getDate() + slaDays)
          const ey = start.getFullYear()
          const em = String(start.getMonth() + 1).padStart(2, '0')
          const ed = String(start.getDate()).padStart(2, '0')
          endDate = `${ey}-${em}-${ed}`
        }

        return {
          ...prev,
          planned_start: startDate,
          planned_end: endDate,
        }
      })
    }
  }

  // Re-calculate SLA end date whenever Start Date changes while a template with sla_hours is active
  useEffect(() => {
    if (!form.task_master_id || !form.planned_start) return
    const selectedTemplate = taskMasterOptions.find((tm) => Number(tm.id) === Number(form.task_master_id))
    const slaHours = Number(selectedTemplate?.sla_hours || 0)
    if (slaHours <= 0) return

    const slaDays = Math.max(1, Math.ceil(slaHours / 24))
    const start = new Date(form.planned_start)
    if (Number.isNaN(start.getTime())) return
    start.setDate(start.getDate() + slaDays)
    const ey = start.getFullYear()
    const em = String(start.getMonth() + 1).padStart(2, '0')
    const ed = String(start.getDate()).padStart(2, '0')
    const newEnd = `${ey}-${em}-${ed}`

    setForm((prev) => {
      if (prev.planned_end === newEnd) return prev
      return { ...prev, planned_end: newEnd }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.planned_start, form.task_master_id])

  return {
    state: {
      form,
      initialProjectName,
      userOptions,
      taskOptions,
      taskMasterOptions,
      isLoading,
      isSaving,
      error,
      success,
      optionError,
      isEditMode: Boolean(editingTaskId),
      editingTaskId,
      completionPercent,
      complexityPoints,
      estimatedEffort,
      riskLevel,
      priorities: PRIORITIES,
      statuses: STATUSES,
      isPool: Boolean(form.is_pool || !form.assigned_to_csv.length),
    },
    actions: {
      patchForm,
      fetchProjectOptions,
      setPriority,
      toggleCompleted,
      addAssignee,
      assignToPool,
      removeAssignee,
      addDependencyTask,
      removeDependencyTask,
      addResponsibleUser,
      removeResponsibleUser,
      updateResponsibleNote,
      toggleResponsibleCompleted,
      syncResponsibleUsers,
      setComplexityPoints,
      setEffortEstimate: (value) => {
        const numeric = Number(value)
        const split = splitDecimalHours(Number.isFinite(numeric) ? numeric : 0)
        setForm((prev) => ({
          ...prev,
          effort_estimate: String(value ?? ''),
          effort_hours: split.hours,
          effort_minutes: split.minutes,
        }))
      },
      setEffortHours: (value) =>
        setForm((prev) => {
          const effort_hours = normalizeHourMinuteInput(value)
          return {
            ...prev,
            effort_hours,
            effort_estimate: String(combineHoursAndMinutes(effort_hours, prev.effort_minutes)),
          }
        }),
      setEffortMinutes: (value) =>
        setForm((prev) => {
          const effort_minutes = String(clampMinutes(value))
          return {
            ...prev,
            effort_minutes,
            effort_estimate: String(combineHoursAndMinutes(prev.effort_hours, effort_minutes)),
          }
        }),
      nudgePriorityUp: () => setPriority(nextPriority(form.priority, -1)),
      nudgePriorityDown: () => setPriority(nextPriority(form.priority, 1)),
      handleTaskMasterChange,
      handleSave,
      handleSaveAndAllocate,
      handleDiscard,
    },
  }
}
